import WebclientMetricsResource from 'flow/api/webclientmetrics';

const statsStore = {
    peers: {},
    // Windows belonging to peers unregistered mid-window — an active-speaker
    // change unsubscribes tiles constantly, and deleting the peer outright
    // would drop the traffic it had already accumulated for this upload.
    departedWindows: [],
    lastSendTime: 0,
    disconnectCount: 0,
    senderInitialized: false,
    collectionIntervalId: null,
    // True while a poll pass is running, so a pass slower than the interval
    // can't overlap the next one (see startMetricsCollection).
    pollInFlight: false,
    name: null,
    // Load-test runs collect stats to render them locally but must not post
    // synthetic peer numbers to Home; the caller decides (see
    // isVideoStatsUploadEnabled). Passed in rather than read here to avoid an
    // import cycle back through meeting-utils.
    uploadEnabled: true,
};

/**
 * The tick rate of a debug session, where peers are actually registered and
 * polled with getStats().
 *
 * 2s rather than a rounder 5s because the per-tile read-outs exist to show how
 * a freshly subscribed tile ramps up after a speaker change, which plays out
 * over ~1-3s — at 5s an entire ramp can fall between two samples. Below ~1s
 * buys nothing: framesPerSecond is itself a browser-side ~1s average, and the
 * bitrate delta grows noisier as its window shrinks.
 *
 * The uploaded aggregate is deliberately insulated from this value; see
 * createPendingWindow.
 */
export const DEBUG_STATS_POLL_INTERVAL_MS = 2000;

/**
 * The tick rate everywhere else — the pre-VID-396 value. Outside debug mode no
 * peers are registered, so each tick only checks whether the 10s disconnect-only
 * send is due; a production client gets nothing for waking 2.5× as often, so it
 * keeps the timer it always had. The caller picks the rate (see
 * startMetricsCollection), the same way it picks uploadEnabled.
 */
export const PRODUCTION_STATS_POLL_INTERVAL_MS = 5000;

/** The window each uploaded payload covers, independent of the poll rate. */
export const STATS_SEND_INTERVAL_MS = 10000;

/**
 * Cap on a single getStats() call. It normally resolves in milliseconds, but a
 * wedged connection can leave it pending forever — and the poll pass awaits
 * peers sequentially, so one hung peer would freeze every read-out and stop
 * uploads. Losing one peer's sample for one tick is the cheap outcome.
 */
const GET_STATS_TIMEOUT_MS = 1500;

const getStatsWithTimeout = (connection) =>
    Promise.race([
        connection.getStats(),
        new Promise((_resolve, reject) => {
            setTimeout(
                () => reject(new Error('getStats timed out')),
                GET_STATS_TIMEOUT_MS,
            );
        }),
    ]);

export const incrementDisconnectCount = () => {
    statsStore.disconnectCount++;
};

export const resetDisconnectCount = () => {
    statsStore.disconnectCount = 0;
};

const statsSubscribers = new Set();

/**
 * Calls `listener` at the end of every poll pass; returns an unsubscribe.
 *
 * Readers used to run their own interval at the same cadence, but such an
 * interval starts whenever the reader mounts and so sits at an arbitrary phase
 * against the poll, adding up to a full interval of staleness on top of the
 * poll's own. Driving readers from the poll caps displayed numbers at one
 * interval old.
 */
export const subscribeToVideoStats = (listener) => {
    statsSubscribers.add(listener);
    return () => {
        statsSubscribers.delete(listener);
    };
};

const notifyStatsSubscribers = () => {
    statsSubscribers.forEach((listener) => {
        try {
            listener();
        } catch (error) {
            console.error('Error notifying video stats subscriber:', error);
        }
    });
};

/**
 * Per-peer accumulator for the window between two uploads.
 *
 * The counts in the payload (packets, bytes, frames, freezes) are per-poll
 * deltas, so reporting only the newest poll would make every uploaded count
 * scale with the poll interval — shortening the poll would silently shrink
 * the freeze counts Home charts, looking like an improvement that never
 * happened. Summing counts and averaging levels across the whole window keeps
 * the payload meaning "per STATS_SEND_INTERVAL_MS" whatever the poll rate is.
 */
const createPendingWindow = () => ({
    counts: {
        packetsReceived: 0,
        packetsLost: 0,
        totalPackets: 0,
        bytesReceived: 0,
        framesDecoded: 0,
        framesDropped: 0,
        totalFrames: 0,
        freezeCount: 0,
    },
    totalFreezesDuration: 0,
    // decodeTime is a per-frame mean, so it is re-weighted by the frames each
    // poll actually decoded instead of averaged flat across polls.
    decodeTimeFrameSum: 0,
    levels: {
        frameRate: [],
        jitter: [],
        bitrate: [],
        width: [],
        height: [],
        roundTripTime: [],
    },
    // Candidate-pair byte counters are cumulative rather than deltas, so the
    // newest sample already is the total — summing would multiply it by the
    // number of polls in the window.
    connection: null,
});

const addLevel = (values, value) => {
    if (value || value === 0) {
        values.push(value);
    }
};

const addToPendingWindow = (pending, stats) => {
    const { video, connection } = stats;
    const { counts, levels } = pending;

    counts.packetsReceived += video.packetsReceived || 0;
    counts.packetsLost += video.packetsLost || 0;
    counts.totalPackets += video.totalPackets || 0;
    counts.bytesReceived += video.bytesReceived || 0;
    counts.framesDecoded += video.framesDecoded || 0;
    counts.framesDropped += video.framesDropped || 0;
    counts.totalFrames += video.totalFrames || 0;
    counts.freezeCount += video.freezeCount || 0;

    pending.totalFreezesDuration += video.totalFreezesDuration || 0;
    pending.decodeTimeFrameSum +=
        (video.decodeTime || 0) * (video.framesDecoded || 0);

    addLevel(levels.frameRate, video.frameRate);
    addLevel(levels.jitter, video.jitter);
    addLevel(levels.bitrate, video.bitrate);
    addLevel(levels.width, video.resolution?.width);
    addLevel(levels.height, video.resolution?.height);

    if (connection?.state) {
        pending.connection = connection;
        addLevel(levels.roundTripTime, connection.currentRoundTripTime);
    }
};

const average = (values) =>
    values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : undefined;

const resetPendingWindows = () => {
    Object.values(statsStore.peers).forEach((peer) => {
        peer.pending = createPendingWindow();
    });
    statsStore.departedWindows = [];
};

const hasTraffic = (pending) =>
    Boolean(pending) &&
    (pending.counts.packetsReceived > 0 || pending.counts.bytesReceived > 0);

/**
 * Every window that carried traffic this send window, including peers that have
 * since been unregistered.
 */
const getActiveWindows = () =>
    [
        ...Object.values(statsStore.peers).map((peer) => peer.pending),
        ...statsStore.departedWindows,
    ].filter(hasTraffic);

const collectMetricsFromWindows = (activeWindows) => {
    const metrics = {
        webrtc_jitter_milliseconds: [],
        webrtc_packets_lost_count: [],
        webrtc_packets_total_count: [],
        webrtc_frames_freeze_count: [],
        webrtc_frames_dropped_count: [],
        webrtc_frames_total_count: [],

        webrtc_packets_received_count: [],
        webrtc_bytes_received_count: [],
        webrtc_frames_decoded_count: [],
        webrtc_framerate_fps: [],
        webrtc_resolution_width_pixels: [],
        webrtc_resolution_height_pixels: [],
        webrtc_bitrate_kbps: [],
        webrtc_decode_time_milliseconds: [],
        webrtc_frames_freeze_duration_milliseconds: [],
        webrtc_frames_freeze_avg_duration_milliseconds: [],

        webrtc_rtt_milliseconds: [],
        webrtc_connection_bytes_received_count: [],
        webrtc_connection_bytes_sent_count: [],
    };

    activeWindows.forEach((pending) => collectWindowMetrics(pending, metrics));
    return metrics;
};

const collectWindowMetrics = (pending, metrics) => {
    const { counts, levels, connection } = pending;

    addMetric(
        metrics.webrtc_jitter_milliseconds,
        average(levels.jitter),
        (v) => v * 1000,
    );
    addMetric(metrics.webrtc_packets_lost_count, counts.packetsLost);
    addMetric(metrics.webrtc_packets_total_count, counts.totalPackets);
    addMetric(metrics.webrtc_frames_dropped_count, counts.framesDropped);
    addMetric(metrics.webrtc_frames_total_count, counts.totalFrames);
    addMetric(metrics.webrtc_frames_freeze_count, counts.freezeCount);
    addMetric(metrics.webrtc_packets_received_count, counts.packetsReceived);
    addMetric(metrics.webrtc_bytes_received_count, counts.bytesReceived);
    addMetric(metrics.webrtc_frames_decoded_count, counts.framesDecoded);
    addMetric(metrics.webrtc_framerate_fps, average(levels.frameRate));
    addMetric(metrics.webrtc_resolution_width_pixels, average(levels.width));
    addMetric(metrics.webrtc_resolution_height_pixels, average(levels.height));
    addMetric(
        metrics.webrtc_bitrate_kbps,
        average(levels.bitrate),
        (v) => v / 1000,
    );
    addMetric(
        metrics.webrtc_decode_time_milliseconds,
        counts.framesDecoded > 0
            ? pending.decodeTimeFrameSum / counts.framesDecoded
            : 0,
        (v) => v * 1000,
    );
    addMetric(
        metrics.webrtc_frames_freeze_duration_milliseconds,
        pending.totalFreezesDuration,
        (v) => v * 1000,
    );
    addMetric(
        metrics.webrtc_frames_freeze_avg_duration_milliseconds,
        counts.freezeCount > 0
            ? pending.totalFreezesDuration / counts.freezeCount
            : 0,
        (v) => v * 1000,
    );

    if (connection) {
        addMetric(
            metrics.webrtc_rtt_milliseconds,
            average(levels.roundTripTime),
            (v) => v * 1000,
        );
        addMetric(
            metrics.webrtc_connection_bytes_received_count,
            connection.bytesReceived,
        );
        addMetric(
            metrics.webrtc_connection_bytes_sent_count,
            connection.bytesSent,
        );
    }
};

const addMetric = (array, value, transform = (v) => v) => {
    if (value || value === 0) {
        array.push(transform(value));
    }
};

const sendAggregatedStats = () => {
    if (!statsStore.uploadEnabled) {
        return;
    }
    try {
        const activeWindows = getActiveWindows();

        if (activeWindows.length === 0 && statsStore.disconnectCount === 0) {
            return;
        }

        if (activeWindows.length === 0) {
            sendDisconnectOnlyMetrics();
            return;
        }

        const metrics = collectMetricsFromWindows(activeWindows);
        const aggregatedStats = aggregateMetrics(metrics, activeWindows.length);

        sendMetricsToServer(aggregatedStats);

        resetDisconnectCount();
    } catch (error) {
        console.error('Error preparing WebRTC metrics:', error);
    }
};

const sendDisconnectOnlyMetrics = () => {
    const disconnectOnlyStats = {
        type: 'incoming_video',
        timestamp: Date.now(),
        metrics: [
            {
                name: 'webrtc_disconnect_count',
                value: String(statsStore.disconnectCount),
            },
            {
                name: 'webrtc_peer_count',
                value: '0',
            },
        ],
    };

    WebclientMetricsResource.registerMetrics(
        {
            onResult() {},
            onError(error) {
                console.error('Failed to send disconnect metrics:', error);
            },
        },
        disconnectOnlyStats,
    );

    resetDisconnectCount();
};

const sendMetricsToServer = (stats) => {
    WebclientMetricsResource.registerMetrics(
        {
            onResult() {},
            onError(error) {
                console.error(
                    'Failed to send aggregated WebRTC metrics:',
                    error,
                );
            },
        },
        stats,
    );
};

const aggregateMetrics = (metrics, activePeerCount) => {
    const aggregatedStats = {
        type: 'incoming_video',
        timestamp: Date.now(),
        metrics: [],
    };

    for (const [metricName, values] of Object.entries(metrics)) {
        if (values.length === 0) continue;

        const isCountMetric = metricName.includes('_count');

        let value;
        if (isCountMetric) {
            value = values.reduce((sum, val) => sum + val, 0);
        } else {
            value = values.reduce((sum, val) => sum + val, 0) / values.length;
        }

        aggregatedStats.metrics.push({
            name: metricName,
            value: isCountMetric
                ? String(Math.round(value))
                : String(Math.round(value * 100) / 100),
        });
    }

    // Peers that carried traffic during the window, not those still registered
    // when it closed. Everything else in the payload now describes the window,
    // and counting only survivors let a payload report real traffic alongside a
    // peer count of zero — which divides badly for anything deriving per-peer
    // averages. Speaker churn does make this exceed the tiles on screen.
    aggregatedStats.metrics.push({
        name: 'webrtc_peer_count',
        value: String(activePeerCount),
    });

    aggregatedStats.metrics.push({
        name: 'webrtc_disconnect_count',
        value: String(statsStore.disconnectCount),
    });

    aggregatedStats.metrics.push({
        name: 'name',
        value: String(statsStore.name),
    });

    return aggregatedStats;
};

const findNetworkType = (stats, candidatePair) => {
    if (!candidatePair || !candidatePair.localCandidateId) {
        return 'unknown';
    }

    const localCandidateId = candidatePair.localCandidateId;
    let localCandidate;

    stats.forEach((report) => {
        if (report.id === localCandidateId) {
            localCandidate = report;
        }
    });

    if (localCandidate) {
        if (localCandidate.networkType) {
            return localCandidate.networkType;
        }

        const candidateType = localCandidate.candidateType || '';
        const protocol = localCandidate.protocol || '';
        if (candidateType) {
            return protocol ? `${candidateType}-${protocol}` : candidateType;
        }
    }

    return 'unknown';
};

const processStats = (currentStats, previousStats, memberName) => {
    const result = {
        memberName,
        timestamp: Date.now(),
        // Whether the snapshot carried a real inbound-rtp video report. `video`
        // stays `{}` (truthy!) when it didn't — before negotiation completes,
        // and forever on closed/failed connections whose getStats() still
        // resolves — so anything that means "is this feed alive" must check
        // this flag, not the object.
        hasInboundVideo: false,
        video: {},
        connection: {},
    };

    currentStats.forEach((report) => {
        if (report.type === 'inbound-rtp' && report.mediaType === 'video') {
            const prev = previousStats?.get(report.id);
            result.hasInboundVideo = true;

            result.video = {
                resolution: {
                    width: report.frameWidth,
                    height: report.frameHeight,
                },
                frameRate: report.framesPerSecond,

                packetsReceived: prev
                    ? report.packetsReceived - prev.packetsReceived
                    : 0,
                packetsLost: prev ? report.packetsLost - prev.packetsLost : 0,

                totalPackets: prev
                    ? report.packetsReceived -
                      prev.packetsReceived +
                      (report.packetsLost - prev.packetsLost)
                    : 0,

                bytesReceived: prev
                    ? report.bytesReceived - prev.bytesReceived
                    : 0,
                framesDecoded: prev
                    ? report.framesDecoded - prev.framesDecoded
                    : 0,
                framesDropped: prev
                    ? report.framesDropped - prev.framesDropped
                    : 0,

                totalFrames: prev
                    ? report.framesDecoded -
                      prev.framesDecoded +
                      (report.framesDropped - prev.framesDropped)
                    : 0,

                freezeCount:
                    prev && report.freezeCount !== undefined
                        ? report.freezeCount - prev.freezeCount
                        : 0,

                totalFreezesDuration:
                    prev && report.totalFreezesDuration !== undefined
                        ? report.totalFreezesDuration -
                          prev.totalFreezesDuration
                        : 0,

                jitter: report.jitter,
                jitterBufferDelay: report.jitterBufferDelay,
            };

            if (
                result.video.freezeCount > 0 &&
                result.video.totalFreezesDuration > 0
            ) {
                result.video.avgFreezeDuration =
                    result.video.totalFreezesDuration /
                    result.video.freezeCount;
            } else {
                result.video.avgFreezeDuration = 0;
            }

            if (prev && report.framesDecoded - prev.framesDecoded > 0) {
                result.video.decodeTime =
                    (report.totalDecodeTime - prev.totalDecodeTime) /
                    (report.framesDecoded - prev.framesDecoded);
            } else {
                result.video.decodeTime = 0;
            }

            if (prev) {
                const timeElapsed = (report.timestamp - prev.timestamp) / 1000;
                if (timeElapsed > 0) {
                    result.video.bitrate =
                        (8 * (report.bytesReceived - prev.bytesReceived)) /
                        timeElapsed;
                }
            }
        }
    });

    let bestCandidatePair = null;
    let highestPriority = -1;

    currentStats.forEach((report) => {
        if (report.type === 'candidate-pair') {
            const isActive =
                report.nominated === true ||
                report.state === 'succeeded' ||
                report.state === 'in-progress' ||
                report.responsesReceived > 0;

            if (isActive && report.priority > highestPriority) {
                bestCandidatePair = report;
                highestPriority = report.priority;
            }
        }
    });

    if (bestCandidatePair) {
        const prev = previousStats?.get(bestCandidatePair.id);

        result.connection = {
            state: bestCandidatePair.state,
            priority: bestCandidatePair.priority,
            nominated: bestCandidatePair.nominated,

            bytesReceived: bestCandidatePair.bytesReceived,
            bytesSent: bestCandidatePair.bytesSent,

            totalRoundTripTime: bestCandidatePair.totalRoundTripTime,
            currentRoundTripTime: bestCandidatePair.currentRoundTripTime,

            requestsSent: prev
                ? bestCandidatePair.requestsSent - prev.requestsSent
                : bestCandidatePair.requestsSent,
            responsesReceived: prev
                ? bestCandidatePair.responsesReceived - prev.responsesReceived
                : bestCandidatePair.responsesReceived,

            networkType: findNetworkType(currentStats, bestCandidatePair),
        };
    }

    return result;
};

/**
 * Inbound video stats for one member as of the last poll, or null when that
 * member has no registered peer (peers are only registered in debug mode) or
 * the first poll hasn't landed yet.
 *
 * Readers get what the collection interval already computed — deliberately no
 * getStats() call of its own, so rendering these numbers costs nothing beyond
 * the polling that debug mode had already started.
 */
export const getVideoStatsForMember = (memberName) => {
    const peer = statsStore.peers[memberName];
    // hasInboundVideo, not `video` truthiness: processStats always returns a
    // (truthy) video object, so the old `!video` guard passed zeros through the
    // moment any poll had landed — a live-looking `0fps · 0kbps` before RTP
    // ever flowed, and forever on dead peers.
    if (!peer?.lastStats?.hasInboundVideo) {
        return null;
    }
    const video = peer.lastStats.video;
    return {
        frameRate: video.frameRate,
        bitrateKbps: video.bitrate ? video.bitrate / 1000 : 0,
        // Total since this peer was registered, not the last poll's delta: a
        // freeze that showed for one interval and vanished was impossible to
        // catch on screen. Resubscribing a tile starts the count over, which is
        // what "freezes on this tile" should mean after a speaker change.
        freezeCount: peer.totalFreezeCount,
        resolution: video.resolution,
        // Lets readers tell a frozen read-out from a live one. Only refreshed
        // by a poll that saw a real inbound-rtp report, so a dead peer's
        // read-out goes stale (and dims) instead of looking freshly polled
        // just because getStats() still resolves on a closed connection.
        lastPolledAt: peer.lastStats.timestamp,
    };
};

export const removePeerFromStats = (memberName) => {
    const peer = statsStore.peers[memberName];

    if (peer) {
        if (hasTraffic(peer.pending)) {
            statsStore.departedWindows.push(peer.pending);
        }
        delete statsStore.peers[memberName];
    }
};

export const startMetricsCollection = (
    userName,
    // The production default is the safe one: a caller that forgets to pass
    // the rate gets the slow timer, not the debug one. Passed in rather than
    // read from the URL here for the same reason as uploadEnabled — reading it
    // needs meeting-utils, which is an import cycle.
    {
        uploadEnabled = true,
        pollIntervalMs = PRODUCTION_STATS_POLL_INTERVAL_MS,
    } = {},
) => {
    if (statsStore.collectionIntervalId) {
        return;
    }

    statsStore.name = userName || 'unknown';
    statsStore.uploadEnabled = uploadEnabled;

    if (!statsStore.lastSendTime) {
        statsStore.lastSendTime = Date.now();
    }

    statsStore.collectionIntervalId = setInterval(async () => {
        // One pass at a time. Without this, a pass slower than the interval
        // (many peers on a saturated load-test host) overlaps the next one:
        // both diff against the same previousStats and both accumulate into the
        // pending window — double-counted uploads, and with out-of-order
        // getStats resolutions, negative deltas on the tiles.
        if (statsStore.pollInFlight) {
            return;
        }
        statsStore.pollInFlight = true;

        try {
            const peerEntries = Object.entries(statsStore.peers);

            for (const [memberName, peerInfo] of peerEntries) {
                if (!peerInfo.connection) continue;

                try {
                    // Race a timeout: getStats() on a wedged connection can
                    // simply never settle, and awaiting it bare would freeze
                    // the whole sequential pass — every plate stuck at full
                    // opacity and no uploads — while leaking one suspended
                    // pass per tick.
                    const stats = await getStatsWithTimeout(
                        peerInfo.connection,
                    );

                    const previousStats = peerInfo.previousStats || null;

                    const processedStats = processStats(
                        stats,
                        previousStats,
                        memberName,
                    );

                    peerInfo.previousStats = new Map();
                    stats.forEach((report) => {
                        peerInfo.previousStats.set(report.id, report);
                    });

                    // Only a snapshot with a real inbound-rtp report refreshes
                    // the read-out. getStats() resolves happily on closed /
                    // failed / never-negotiated connections; treating any
                    // resolution as "freshly polled" kept lastPolledAt current
                    // forever, so the plate's staleness dimming could never
                    // fire in exactly the dead-peer case it exists for.
                    if (processedStats.hasInboundVideo) {
                        peerInfo.lastStats = processedStats;
                    }

                    if (!peerInfo.pending) {
                        peerInfo.pending = createPendingWindow();
                    }
                    addToPendingWindow(peerInfo.pending, processedStats);

                    peerInfo.totalFreezeCount +=
                        processedStats.video.freezeCount || 0;

                    peerInfo.connectionState =
                        peerInfo.connection.connectionState;
                } catch (error) {
                    console.error(
                        `Error processing stats for ${memberName}:`,
                        error,
                    );
                }
            }

            notifyStatsSubscribers();

            if (
                Date.now() - statsStore.lastSendTime >=
                STATS_SEND_INTERVAL_MS
            ) {
                sendAggregatedStats();
                statsStore.lastSendTime = Date.now();
                // Cleared on every send tick rather than only after a successful
                // upload, so a ?debug=local run does not accumulate its windows
                // for the whole meeting.
                resetPendingWindows();
            }
        } finally {
            statsStore.pollInFlight = false;
        }
    }, pollIntervalMs);
};

export const addPeerForStats = (peerConnection, memberName) => {
    // Route an overwrite through the same salvage as an unregister. A signaling
    // reconnect re-subscribes without ever calling removePeerFromStats (the
    // peer slots are reset to {} directly), so registering the new peer used to
    // silently drop up to a full send window of pre-reconnect traffic — exactly
    // the window around a disconnect, when the numbers matter most.
    removePeerFromStats(memberName);
    statsStore.peers[memberName] = {
        connection: peerConnection,
        previousStats: null,
        lastStats: null,
        pending: createPendingWindow(),
        // Freezes since this peer was registered, for the tile read-out.
        totalFreezeCount: 0,
        connectionState: peerConnection.connectionState,
        startTime: Date.now(),
    };
};

export const stopMetricsCollection = () => {
    if (statsStore.collectionIntervalId) {
        clearInterval(statsStore.collectionIntervalId);
        statsStore.collectionIntervalId = null;
    }

    statsStore.lastSendTime = 0;
    statsStore.uploadEnabled = true;
    // The whole session's state, not just the windows: meeting teardown
    // (stopMeeting -> leaveVideoRoom) disposes receiver peers without
    // removePeerFromStats, and member names carry per-join UUIDs — so surviving
    // entries would be getStats()-polled for the rest of the tab's life and,
    // worse, their unsent pending windows would ride into the NEXT meeting's
    // first upload under the new session's name (a ?debug=local bot's synthetic
    // traffic leaking into a later uploading session — the exact contamination
    // that mode exists to prevent). Same reasoning for the disconnect counter.
    // Nothing will drain any of it once the interval is gone.
    statsStore.peers = {};
    statsStore.departedWindows = [];
    statsStore.disconnectCount = 0;
};
